<template>
  <component
    :is="determineComponent"
    :to="to"
    :class="{ card: true, raised, clickable: to, 'vs-accent': accent }"
    :style="{ borderColor: accent }"
    active-class="is-active"
  >
    <div v-if="title" class="card-header">
      <vs-heading>{{ title }}</vs-heading>
    </div>
    <div :class="{ 'card-content': true, 'no-content-padding': noContentPadding }">
      <vs-wrapper>
        <slot />
      </vs-wrapper>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import type { RouterLinkProps } from 'vue-router/types/router'

/**
 * Cards are used to group related information and tasks to make things easier
 * to scan, and action upon
 */
type RouterLinkTo = RouterLinkProps['to']
const props = withDefaults(
  defineProps<{
    /**
     * The title of the card
     */
    title?: string | null
    /**
     * Removes the default padding on the content
     */
    noContentPadding?: boolean
    /**
     * Adds a box shadow
     */
    raised?: boolean
    /**
     * A router link
     */
    to?: RouterLinkTo
    /**
     * Adds a border of color on the left side of the card
     */
    accent?: string | null
  }>(),
  {
    title: null,
    noContentPadding: false,
    raised: false,
    accent: null,
  },
)
const determineComponent = computed(() => (props.to ? 'router-link' : 'div'))
</script>

<style scoped>
.card {
  display: block;
  background-color: var(--color-white);
  border: 1px solid var(--color-grey--lighter);
  border-radius: var(--space-base);
  box-sizing: border-box;
  text-decoration: none;
  color: inherit;
}

.card-header,
.card-content:not(.no-content-padding) {
  padding: var(--space-base);
  box-sizing: border-box;
}

.card-header {
  padding-bottom: 0;
}

.raised,
.clickable {
  border: none;
  box-shadow: var(--shadow-low);
  transition: all 200ms;
}

.clickable:hover {
  box-shadow: var(--shadow-high);
}

.clickable:active {
  transform: scale(0.97);
}

.vs-accent {
  border-left: 4px solid var(--color-grey--lightest);
}

.is-active {
  box-shadow: var(--shadow-high);
}
</style>
